/*
 * Đưa lịch sử chạm lên server. Mọi nơi ghi touches đều đi qua store nên theo dõi store, không phải sửa từng chỗ.
 * Lỗi mạng thì giữ trong hàng đợi trên máy để gửi lại; client_id giữ nguyên nên gửi lại không bị trùng.
 */
import dayjs from 'dayjs';
import { apiRequest } from './api';
import { isDemoId } from './demo';
import type { LeadLocal, Touch } from './logic';
import { useLeadStore } from './store';

const KEY = 'khothi-tele-touch-outbox';
const RETRY_MS = 60_000;

/** owner: tài khoản ghi dòng này — server lấy người ghi theo phiên đăng nhập nên chỉ gửi bằng đúng phiên của người đó */
interface Item { leadId: number; touch: Touch; owner?: string }

/** Tài khoản đang đăng nhập ('' = chưa đăng nhập) */
export const currentOwner = () => {
  try {
    if (!localStorage.getItem('token')) return '';
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const id = u?.info?.id ?? u?.id;
    return id != null ? `${u?.type ?? u?.info?.type ?? ''}:${id}` : '';
  } catch {
    return '';
  }
};

const load = (): Item[] => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
};
let outbox = load();
const save = () => { try { localStorage.setItem(KEY, JSON.stringify(outbox)); } catch { /* đầy bộ nhớ: lần gửi kế tiếp vẫn còn trong RAM */ } };

/** Dòng mới do FE ghi trên khách thật; dòng tải từ server (server: true) và khách mẫu thì bỏ qua */
export const newTouches = (prev: Record<number, LeadLocal>, next: Record<number, LeadLocal>): Item[] => {
  const out: Item[] = [];
  Object.entries(next).forEach(([k, l]) => {
    const old = prev[Number(k)];
    if (old === l || isDemoId(Number(k))) return;
    const seen = new Set((old?.touches ?? []).map((t) => t.id));
    l.touches.forEach((t) => { if (!seen.has(t.id) && !t.server) out.push({ leadId: Number(k), touch: t }); });
  });
  return out;
};

const send = ({ leadId, touch: t }: Item) => apiRequest(`/api/v2/telesale/customers/${leadId}/touches`, {
  method: 'POST',
  body: JSON.stringify({
    client_id: t.id,
    kind: t.kind,
    ...(t.dir ? { direction: t.dir } : {}),
    ...(t.dur != null ? { duration: t.dur } : {}),
    ...(t.code ? { code: t.code } : {}),
    ...(t.text ? { text: t.text } : {}),
    // Server không nhận giờ ở tương lai; đồng hồ máy có thể chạy nhanh
    at: dayjs(Math.min(t.at, Date.now())).format('YYYY-MM-DD HH:mm:ss'),
  }),
});

/** Lỗi mạng, lỗi máy chủ, quá tải (408, 429): gửi lại sau. Dữ liệu sai (4xx khác) có gửi lại cũng vậy nên bỏ */
export const retryable = (status?: number) => !status || status >= 500 || status === 408 || status === 429;

let flushing = false;
let timer: ReturnType<typeof setTimeout> | undefined;
export async function flushTouches() {
  if (flushing) return;
  if (timer) { clearTimeout(timer); timer = undefined; }
  const me = currentOwner();
  const mine = (it: Item) => !it.owner || it.owner === me;
  // Chưa đăng nhập thì không gọi API: 401 làm đăng xuất về /login, trang /login lại gửi tiếp thành vòng tải lại trang
  if (!me) {
    if (outbox.length) timer = setTimeout(flushTouches, RETRY_MS);
    return;
  }
  flushing = true;
  for (let it = outbox.find(mine); it; it = outbox.find(mine)) {
    // eslint-disable-next-line no-await-in-loop
    const res = await send(it);
    if (!res.ok && retryable(res.status)) break;
    const done = it;
    outbox = outbox.filter((x) => x !== done);
    save();
  }
  flushing = false;
  // Dòng của tài khoản khác giữ lại, chờ người đó đăng nhập
  if (outbox.some(mine)) timer = setTimeout(flushTouches, RETRY_MS);
}

if (process.env.NODE_ENV !== 'test') {
  useLeadStore.subscribe((s, prev) => {
    if (s.byId === prev.byId) return;
    const fresh = newTouches(prev.byId, s.byId);
    if (!fresh.length) return;
    const owner = currentOwner() || undefined;
    outbox = [...outbox, ...fresh.map((it) => ({ ...it, owner }))];
    save();
    flushTouches();
  });
  window.addEventListener('online', () => { flushTouches(); });
  // Chờ các module nạp xong: có thể còn dòng chưa gửi từ lần mở trước
  setTimeout(flushTouches, 0);
}
